import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plugs, PlugsConnected, ArrowClockwise, CheckCircle, XCircle,
  Trash, Download, PencilSimple,
} from "@phosphor-icons/react";

export default function Plugins() {
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [installing, setInstalling] = useState(null); // catalog item
  const [configEditor, setConfigEditor] = useState(null); // installed item
  const [testing, setTesting] = useState({});
  const [syncing, setSyncing] = useState({});

  const load = async () => {
    const [c, i] = await Promise.all([
      api.get("/admin/plugins/catalog"),
      api.get("/admin/plugins"),
    ]);
    setCatalog(c.data);
    setInstalled(i.data);
  };

  useEffect(() => { load(); }, []);

  const install = async (config) => {
    try {
      await api.post("/admin/plugins", {
        plugin_type: installing.plugin_type,
        name: installing.display_name,
        config, enabled: true,
      });
      toast.success(`${installing.display_name} installed`);
      setInstalling(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const saveConfig = async (newConfig, enabled, name) => {
    try {
      await api.patch(`/admin/plugins/${configEditor.plugin_id}`, {
        config: newConfig, enabled, name,
      });
      toast.success("Plugin updated");
      setConfigEditor(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const testPlugin = async (p) => {
    setTesting({ ...testing, [p.plugin_id]: true });
    try {
      const { data } = await api.post(`/admin/plugins/${p.plugin_id}/test`);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setTesting({ ...testing, [p.plugin_id]: false });
    }
  };

  const sync = async (p) => {
    setSyncing({ ...syncing, [p.plugin_id]: true });
    try {
      const { data } = await api.post(`/admin/plugins/${p.plugin_id}/sync`);
      const s = data.stats;
      toast.success(
        `Sync complete: +${s.created} new, ${s.updated} updated, ${s.hierarchy_links} manager links, ${s.departments_created} depts`
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSyncing({ ...syncing, [p.plugin_id]: false });
    }
  };

  const uninstall = async (p) => {
    if (!window.confirm(`Uninstall ${p.name}? Employees already synced will remain.`)) return;
    try {
      await api.delete(`/admin/plugins/${p.plugin_id}`);
      toast.success("Plugin uninstalled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="plugins-page">
      {/* Installed */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <PlugsConnected size={18} className="text-[#E1252A]" />
          <h3 className="text-lg font-semibold tracking-tight">Installed plugins</h3>
          <div className="text-xs text-zinc-500 uppercase tracking-wider">({installed.length})</div>
        </div>
        {installed.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-zinc-500" data-testid="installed-empty">
            No HR plugins installed yet. Pick one from the catalog below.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {installed.map((p) => (
              <div key={p.plugin_id} className="border border-border bg-background p-4 space-y-3" data-testid={`installed-${p.plugin_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold">{p.name}</div>
                      {p.enabled ? (
                        <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-emerald-400 text-emerald-700 dark:text-emerald-300">enabled</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-sm text-[10px] font-mono opacity-60">disabled</Badge>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mt-0.5">{p.plugin_type}</div>
                  </div>
                  <div className="text-right text-[10px] text-zinc-500 uppercase tracking-wider">
                    {p.last_sync ? (
                      <>
                        <div>Last sync</div>
                        <div className="text-[11px] normal-case text-zinc-700 dark:text-zinc-300 mt-0.5">
                          {new Date(p.last_sync).toLocaleString()}
                        </div>
                      </>
                    ) : <span>Never synced</span>}
                  </div>
                </div>
                {p.last_sync_stats && (
                  <div className="grid grid-cols-4 gap-2 text-[11px] pt-2 border-t border-border">
                    <Stat label="Seen" value={p.last_sync_stats.employees_seen} />
                    <Stat label="New" value={p.last_sync_stats.created} accent />
                    <Stat label="Updated" value={p.last_sync_stats.updated} />
                    <Stat label="Mgr links" value={p.last_sync_stats.hierarchy_links} />
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                  <Button size="sm" variant="outline" onClick={() => testPlugin(p)} disabled={testing[p.plugin_id]} className="rounded-sm" data-testid={`test-${p.plugin_id}`}>
                    <CheckCircle size={12} className="mr-1" /> {testing[p.plugin_id] ? "Testing…" : "Test"}
                  </Button>
                  <Button size="sm" onClick={() => sync(p)} disabled={syncing[p.plugin_id] || !p.enabled} className="rounded-sm bg-foreground text-background" data-testid={`sync-${p.plugin_id}`}>
                    <ArrowClockwise size={12} className={`mr-1 ${syncing[p.plugin_id] ? "animate-spin" : ""}`} />
                    {syncing[p.plugin_id] ? "Syncing…" : "Sync now"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfigEditor(p)} className="rounded-sm" data-testid={`config-${p.plugin_id}`}>
                    <PencilSimple size={12} className="mr-1" /> Configure
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => uninstall(p)} className="rounded-sm text-red-600 border-red-200 ml-auto" data-testid={`uninstall-${p.plugin_id}`}>
                    <Trash size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Catalog */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Plugs size={18} />
          <h3 className="text-lg font-semibold tracking-tight">Available plugins</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {catalog.map((c) => (
            <div key={c.plugin_type} className="border border-border bg-background p-4 space-y-3 flex flex-col" data-testid={`catalog-${c.plugin_type}`}>
              <div>
                <div className="text-base font-semibold">{c.display_name}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{c.plugin_type}</div>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed flex-1">{c.description}</p>
              <Button onClick={() => setInstalling(c)} variant="outline" className="rounded-sm w-full" data-testid={`install-${c.plugin_type}`}>
                <Download size={12} className="mr-1" /> Install
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Install config dialog */}
      {installing && (
        <ConfigDialog
          title={`Install ${installing.display_name}`}
          schema={installing.config_schema}
          initialConfig={{}}
          onCancel={() => setInstalling(null)}
          onSubmit={(cfg) => install(cfg)}
          submitLabel="Install"
        />
      )}

      {/* Edit config dialog */}
      {configEditor && (
        <ConfigDialog
          title={`Configure ${configEditor.name}`}
          schema={(catalog.find((c) => c.plugin_type === configEditor.plugin_type) || {}).config_schema || {}}
          initialConfig={configEditor.config || {}}
          initialEnabled={configEditor.enabled}
          initialName={configEditor.name}
          allowEnableToggle
          onCancel={() => setConfigEditor(null)}
          onSubmit={(cfg, enabled, name) => saveConfig(cfg, enabled, name)}
          submitLabel="Save"
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-zinc-500 uppercase tracking-wider text-[9px]">{label}</div>
      <div className={`font-mono ${accent ? "text-[#E1252A] font-semibold" : ""}`}>{value}</div>
    </div>
  );
}

function ConfigDialog({ title, schema, initialConfig, initialEnabled = true, initialName, allowEnableToggle, onCancel, onSubmit, submitLabel }) {
  const [cfg, setCfg] = useState({ ...initialConfig });
  const [enabled, setEnabled] = useState(initialEnabled);
  const [name, setName] = useState(initialName || "");

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const keys = Object.keys(schema || {});

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="plugin-config-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {initialName !== undefined && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Display name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="plugin-name-input" />
            </div>
          )}
          {keys.length === 0 && (
            <div className="text-sm text-zinc-500">No configuration needed for this plugin.</div>
          )}
          {keys.map((k) => {
            const s = schema[k];
            if (s.type === "boolean") {
              return (
                <div key={k} className="flex items-center justify-between border border-border p-3">
                  <div>
                    <div className="text-sm">{s.label || k}</div>
                    {s.description && <div className="text-xs text-zinc-500">{s.description}</div>}
                  </div>
                  <Switch checked={!!cfg[k]} onCheckedChange={(v) => setField(k, v)} data-testid={`config-${k}`} />
                </div>
              );
            }
            if (s.type === "textarea") {
              return (
                <div key={k}>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">{s.label || k}</Label>
                  <Textarea value={cfg[k] || ""} onChange={(e) => setField(k, e.target.value)} rows={6} className="rounded-sm font-mono text-xs" placeholder={s.placeholder} data-testid={`config-${k}`} />
                </div>
              );
            }
            return (
              <div key={k}>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">{s.label || k}</Label>
                <Input
                  type={s.type === "password" ? "password" : "text"}
                  value={cfg[k] || ""}
                  onChange={(e) => setField(k, e.target.value)}
                  placeholder={s.placeholder}
                  className="rounded-sm"
                  data-testid={`config-${k}`}
                />
              </div>
            );
          })}
          {allowEnableToggle && (
            <div className="flex items-center justify-between border border-border p-3">
              <div className="text-sm">Enabled</div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="config-enabled" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-sm" data-testid="plugin-cancel-btn">Cancel</Button>
          <Button onClick={() => onSubmit(cfg, enabled, name)} className="rounded-sm bg-foreground text-background" data-testid="plugin-submit-btn">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
